import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Send, Phone, Video, ChevronRight, MessageCircle, Users, Loader, X, Mic, MicOff, Search, Hash, Megaphone, Plus, UserPlus, LogOut, Image as ImageIcon, Paperclip, FileText } from 'lucide-react';
import { openRazorpayCheckout } from '../../utils/razorpay';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatTab({ user, paymentSession, clearPaymentSession, showToast, onProfileClick }) {
  const [chatView, setChatView] = useState('dms');
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [dmPaymentRequired, setDmPaymentRequired] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [upcomingBooking, setUpcomingBooking] = useState(null);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  // Rooms state
  const [joinedRooms, setJoinedRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [roomMessages, setRoomMessages] = useState([]);
  const [roomSearch, setRoomSearch] = useState('');
  const [searchedRooms, setSearchedRooms] = useState([]);
  const [searchingRooms, setSearchingRooms] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(null);
  const SMART_REPLIES = ['Hi there! 👋', 'Sounds great!', 'Let me check', 'Thanks!', 'Sure, when works?', 'Got it 👍', 'Can we schedule a call?'];
  // Communities state
  const [joinedCommunities, setJoinedCommunities] = useState([]);
  const [activeCommunity, setActiveCommunity] = useState(null);
  const [communityBroadcasts, setCommunityBroadcasts] = useState([]);
  // Birthday state
  const [birthdaysToday, setBirthdaysToday] = useState([]);
  const [bdayWishing, setBdayWishing] = useState(null);

  const messagesEnd = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };

  useEffect(() => { fetchConversations(); fetchJoinedRooms(); fetchJoinedCommunities(); fetchBirthdays(); }, []);

  // Payment polling
  useEffect(() => {
    if (!paymentSession) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 5) { if (clearPaymentSession) clearPaymentSession(); return; }
      try {
        const res = await fetch(`${API_URL}/api/payments/status/${paymentSession}`, { headers });
        if (res.ok) {
          const d = await res.json();
          if (d.status === 'paid') {
            if (showToast) showToast('Payment confirmed!');
            if (clearPaymentSession) clearPaymentSession();
            fetchConversations();
            return;
          }
        }
      } catch {}
      attempts++;
      setTimeout(poll, 2000);
    };
    poll();
  }, [paymentSession]);

  // ===== DM Functions =====
  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/dm/conversations`, { headers });
      if (res.ok) setConversations(await res.json());
    } catch {}
    setLoading(false);
  };

  const fetchBirthdays = async () => {
    try {
      const res = await fetch(`${API_URL}/api/birthdays/today`, { headers });
      if (res.ok) setBirthdaysToday(await res.json());
    } catch {}
  };

  const sendBirthdayWish = async (uid) => {
    setBdayWishing(uid);
    try {
      const res = await fetch(`${API_URL}/api/birthdays/wish/${uid}`, { method: 'POST', headers });
      if (res.ok) {
        const d = await res.json();
        showToast?.(d.paid ? `Birthday wish sent (${d.price} charged)` : 'Birthday wish sent — Free!');
        // Remove from list locally
        setBirthdaysToday(prev => prev.filter(u => u.user_id !== uid));
        fetchConversations();
      } else {
        const err = await res.json().catch(()=>({}));
        showToast?.(err.detail || 'Failed to send wish');
      }
    } catch { showToast?.('Network error'); }
    setBdayWishing(null);
  };

  const openChat = async (convo) => {
    setActiveChat(convo); setActiveRoom(null); setActiveCommunity(null);
    setDmPaymentRequired(null); setUpcomingBooking(null);
    try {
      const [msgsRes, payReqRes, bookingsRes] = await Promise.all([
        fetch(`${API_URL}/api/dm/${convo.user.user_id}`, { headers }),
        fetch(`${API_URL}/api/dm/payment-required/${convo.user.user_id}`, { headers }),
        fetch(`${API_URL}/api/bookings/history/${convo.user.user_id}`, { headers }),
      ]);
      if (msgsRes.ok) setMessages(await msgsRes.json());
      if (payReqRes.ok) setDmPaymentRequired(await payReqRes.json());
      if (bookingsRes.ok) {
        const bookings = await bookingsRes.json();
        const active = bookings.find(b => b.status === 'upcoming' || b.status === 'in_progress');
        if (active) setUpcomingBooking(active);
      }
    } catch {}
    setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !activeChat) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/dm/send?recipient_id=${activeChat.user.user_id}&message=${encodeURIComponent(newMsg)}`, { method: 'POST', headers });
      if (res.ok) {
        const dm = await res.json();
        setMessages(prev => [...prev, dm]);
        setNewMsg('');
        setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      }
    } catch {}
    setSending(false);
  };

  const startPayment = async (expertId, paymentType) => {
    setPaymentLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ expert_id: expertId, payment_type: paymentType }),
      });
      if (res.ok) {
        const d = await res.json();
        const rzpResponse = await openRazorpayCheckout({
          order_id: d.order_id, amount: d.amount, currency: d.currency,
          key_id: d.key_id, name: 'Connect',
          description: `${paymentType.replace('_', ' ')} with ${d.expert_name}`,
          prefill: { name: user?.name, email: user?.email },
        });
        const verifyRes = await fetch(`${API_URL}/api/payments/verify`, {
          method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...rzpResponse, txn_id: d.txn_id }),
        });
        if (verifyRes.ok) {
          if (showToast) showToast('Payment successful!');
          fetchConversations();
          setDmPaymentRequired(null);
        }
      }
    } catch (e) {
      if (e.message !== 'Payment cancelled' && showToast) showToast(e.message || 'Payment failed');
    }
    setPaymentLoading(false);
  };

  const walletPay = async (expertId, paymentType) => {
    setPaymentLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/wallet/pay`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ expert_id: expertId, payment_type: paymentType }),
      });
      if (res.ok) {
        const d = await res.json();
        if (showToast) showToast(`Paid ₹${d.amount} from wallet! Balance: ₹${d.new_balance}`);
        fetchConversations();
        setDmPaymentRequired(null);
      } else {
        const err = await res.json();
        if (showToast) showToast(err.detail || 'Payment failed');
      }
    } catch {}
    setPaymentLoading(false);
  };

  // Voice note
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        if (blob.size < 500) return;
        const fd = new FormData();
        fd.append('file', blob, 'voice_note.webm');
        fd.append('recipient_id', activeChat?.user?.user_id || '');
        try {
          const uploadRes = await fetch(`${API_URL}/api/voice-note/upload`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
          if (uploadRes.ok) {
            const data = await uploadRes.json();
            await fetch(`${API_URL}/api/dm/send`, {
              method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
              body: JSON.stringify({ recipient_id: activeChat.user.user_id, content: `[Voice Note] ${data.audio_url}`, voice_url: data.audio_url }),
            });
            openChat(activeChat);
          }
        } catch {}
      };
      recorder.start();
      setMediaRecorder(recorder);
      setRecording(true);
      setTimeout(() => { if (recorder.state === 'recording') { recorder.stop(); setRecording(false); } }, 60000);
    } catch {}
  };
  const stopRecording = () => { if (mediaRecorder?.state === 'recording') { mediaRecorder.stop(); setRecording(false); } };

  // ===== Rooms Functions =====
  const fetchJoinedRooms = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chatrooms/joined`, { headers: { ...headers, 'Content-Type': 'application/json' } });
      if (res.ok) setJoinedRooms(await res.json());
    } catch {}
  };

  const searchRooms = async (q) => {
    setRoomSearch(q);
    if (!q.trim()) { setSearchedRooms([]); return; }
    setSearchingRooms(true);
    try {
      const res = await fetch(`${API_URL}/api/chatrooms?q=${encodeURIComponent(q)}&limit=20`, { headers: { ...headers, 'Content-Type': 'application/json' } });
      if (res.ok) setSearchedRooms(await res.json());
    } catch {}
    setSearchingRooms(false);
  };

  const joinRoom = async (roomId) => {
    try {
      await fetch(`${API_URL}/api/chatrooms/${roomId}/join`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' } });
      fetchJoinedRooms();
      if (showToast) showToast('Joined room!');
    } catch {}
  };

  const createCustomRoom = async () => {
    const subject = (roomSearch || '').trim();
    if (!subject) return;
    try {
      const res = await fetch(`${API_URL}/api/chatrooms/custom?name=${encodeURIComponent(subject)}&subject_path=${encodeURIComponent(subject)}`,
        { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' } });
      if (res.ok) {
        const newRoom = await res.json();
        // Auto-join the freshly created room
        if (newRoom?.room_id) {
          await fetch(`${API_URL}/api/chatrooms/${newRoom.room_id}/join`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' } });
          fetchJoinedRooms();
          openRoom(newRoom);
          showToast?.(`Room "${subject}" created!`);
        }
      } else {
        showToast?.('Failed to create room');
      }
    } catch { showToast?.('Network error'); }
  };

  const leaveRoom = async (roomId) => {
    try {
      await fetch(`${API_URL}/api/chatrooms/${roomId}/leave`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' } });
      fetchJoinedRooms();
      setActiveRoom(null);
      if (showToast) showToast('Left room');
    } catch {}
  };

  const openRoom = async (room) => {
    setActiveRoom(room); setActiveChat(null); setActiveCommunity(null);
    try {
      const res = await fetch(`${API_URL}/api/chatrooms/${room.room_id}/messages?limit=50`, { headers: { ...headers, 'Content-Type': 'application/json' } });
      if (res.ok) setRoomMessages(await res.json());
    } catch {}
    setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const sendRoomMessage = async () => {
    if (!newMsg.trim() || !activeRoom) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/chatrooms/${activeRoom.room_id}/messages?content=${encodeURIComponent(newMsg)}`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const msg = await res.json();
        setRoomMessages(prev => [...prev, msg]);
        setNewMsg('');
      }
    } catch {}
    setSending(false);
    setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  // ===== Communities Functions =====
  const fetchJoinedCommunities = async () => {
    try {
      const res = await fetch(`${API_URL}/api/communities/joined/list`, { headers: { ...headers, 'Content-Type': 'application/json' } });
      if (res.ok) setJoinedCommunities(await res.json());
    } catch {}
  };

  const openCommunity = async (comm) => {
    setActiveCommunity(comm); setActiveChat(null); setActiveRoom(null);
    try {
      const res = await fetch(`${API_URL}/api/communities/${comm.community_id}/broadcasts`, { headers: { ...headers, 'Content-Type': 'application/json' } });
      if (res.ok) setCommunityBroadcasts(await res.json());
    } catch {}
    setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const sendBroadcast = async () => {
    if (!newMsg.trim() || !activeCommunity) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/communities/${activeCommunity.community_id}/broadcast?content=${encodeURIComponent(newMsg)}`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const b = await res.json();
        setCommunityBroadcasts(prev => [b, ...prev]);
        setNewMsg('');
      }
    } catch {}
    setSending(false);
  };

  const leaveCommunity = async (communityId) => {
    try {
      await fetch(`${API_URL}/api/communities/${communityId}/leave`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' } });
      fetchJoinedCommunities();
      setActiveCommunity(null);
      if (showToast) showToast('Left community');
    } catch {}
  };

  // Reusable exit confirmation modal — renders at any view level
  const ExitConfirmModal = () => showExitConfirm ? (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center" onClick={() => setShowExitConfirm(null)} data-testid="exit-confirm-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 w-full max-w-xs mx-4" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-white text-base mb-2">Leave Community?</h3>
        <p className="text-sm text-zinc-400 mb-5">Are you sure you want to exit this community?</p>
        <div className="flex gap-3">
          <button onClick={() => setShowExitConfirm(null)} className="flex-1 bg-zinc-800 border border-zinc-700 text-white font-bold rounded-xl py-2.5 text-sm" data-testid="exit-cancel">Cancel</button>
          <button onClick={async () => { await leaveCommunity(showExitConfirm); setShowExitConfirm(null); }}
            className="flex-1 bg-red-600 text-white font-bold rounded-xl py-2.5 text-sm" data-testid="exit-confirm">Yes, Leave</button>
        </div>
      </div>
    </div>
  ) : null;

  // ===== Active views for inner chat/room/community =====
  const hasActiveView = activeChat || activeRoom || activeCommunity;

  // ===== RENDER: Active DM Chat =====
  if (activeChat) {
    return (
      <div className="h-full flex flex-col bg-black" data-testid="active-chat">
        <div className="flex items-center space-x-3 px-4 py-3 border-b border-zinc-800 shrink-0">
          <button onClick={() => setActiveChat(null)} data-testid="chat-back"><ArrowLeft className="w-5 h-5" /></button>
          <button onClick={() => onProfileClick?.(activeChat.user?.user_id)} className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-orange-400 p-[1.5px] shrink-0">
              <div className="w-full h-full rounded-full bg-black flex items-center justify-center overflow-hidden">
                {activeChat.user?.picture ? <img src={activeChat.user.picture} alt="" className="w-full h-full object-cover" /> :
                  <span className="text-xs font-bold">{activeChat.user?.name?.[0]}</span>}
              </div>
            </div>
            <p className="font-semibold text-sm truncate">{activeChat.user?.name}</p>
          </button>
          <button onClick={() => walletPay(activeChat.user.user_id, 'voice_call')}
            className="text-zinc-400 hover:text-green-400 transition-colors p-2" data-testid="pay-voice-call"
            disabled={paymentLoading}><Phone className="w-5 h-5" /></button>
          <button onClick={() => walletPay(activeChat.user.user_id, 'video_call')}
            className="text-zinc-400 hover:text-blue-400 transition-colors p-2" data-testid="pay-video-call"
            disabled={paymentLoading}><Video className="w-5 h-5" /></button>
          {paymentLoading && <Loader className="w-4 h-4 text-zinc-500 animate-spin" />}
        </div>

        {/* Upcoming Call Banner */}
        {upcomingBooking && (
          <div className="border-b border-zinc-800 shrink-0 px-4 py-3" data-testid="call-banner">
            <div className="flex items-center justify-between bg-gradient-to-r from-green-900/40 to-emerald-900/40 border border-green-800/50 rounded-xl px-4 py-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${upcomingBooking.status === 'in_progress' ? 'bg-green-500 animate-pulse' : 'bg-green-500/20'}`}>
                  {upcomingBooking.call_type === 'voice' ? <Phone className="w-5 h-5 text-white" /> : <Video className="w-5 h-5 text-white" />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{upcomingBooking.status === 'in_progress' ? 'Call in Progress' : 'Upcoming Call'}</p>
                  <p className="text-xs text-zinc-400">{upcomingBooking.call_type === 'voice' ? 'Voice' : 'Video'} call</p>
                </div>
              </div>
              <button onClick={() => window.location.href = `/call/${upcomingBooking.booking_id}`}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-full text-sm font-medium transition-colors"
                data-testid="join-call-btn"><Phone className="w-4 h-4" />Join</button>
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {messages.map(msg => {
            const isMine = msg.sender_id === user.user_id;
            return (
              <div key={msg.dm_id} className={`flex items-end gap-2 ${isMine ? 'justify-end' : 'justify-start'}`}>
                {!isMine && (
                  <button onClick={() => onProfileClick?.(msg.sender_id)} className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center overflow-hidden shrink-0 mb-4">
                    {activeChat?.user?.picture ? <img src={activeChat.user.picture} alt="" className="w-full h-full object-cover" /> :
                      <span className="text-[9px] font-bold">{activeChat?.user?.name?.[0]}</span>}
                  </button>
                )}
                <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${isMine ? 'bg-white text-black rounded-br-sm' : 'bg-zinc-800 text-white rounded-bl-sm'}`}>
                  {msg.dm_type === 'voice_note' || msg.message?.startsWith('[Voice Note]') ? (
                    <div className="flex items-center space-x-2">
                      <Mic className="w-4 h-4 text-purple-400" />
                      <audio controls className="h-8 max-w-[200px]" src={msg.voice_url || msg.message?.replace('[Voice Note] ', '')} />
                    </div>
                  ) : msg.dm_type === 'file_share' && msg.media_path ? (
                    <a href={`${API_URL}/api/storage/${msg.media_path}`} target="_blank" rel="noreferrer"
                      className={`flex items-center gap-2 underline ${isMine ? 'text-blue-800' : 'text-blue-300'}`}
                      data-testid={`file-msg-${msg.dm_id}`}>
                      <FileText className="w-4 h-4 shrink-0" />
                      <span className="text-sm truncate max-w-[180px]">{msg.filename || msg.message?.replace('📎 ', '')}</span>
                    </a>
                  ) : <p className="text-sm">{msg.message}</p>}
                  <span className={`text-[9px] ${isMine ? 'text-zinc-400' : 'text-zinc-500'}`}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            );
          })}
          <div ref={messagesEnd} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-zinc-800 shrink-0">
          {dmPaymentRequired?.required && messages.length === 0 && (
            <div className="flex gap-2 mb-2">
              <button onClick={() => walletPay(activeChat.user.user_id, 'dm')} disabled={paymentLoading}
                className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-emerald-600 to-green-500 text-white font-semibold text-sm py-3 rounded-xl disabled:opacity-50"
                data-testid="wallet-pay-dm-btn">
                <span>{paymentLoading ? 'Processing...' : `Wallet Pay ₹${dmPaymentRequired.price || 5}`}</span>
              </button>
              <button onClick={() => startPayment(activeChat.user.user_id, 'dm')} disabled={paymentLoading}
                className="flex-1 flex items-center justify-center space-x-2 bg-zinc-800 border border-zinc-700 text-white font-semibold text-sm py-3 rounded-xl disabled:opacity-50"
                data-testid="card-pay-dm-btn">
                <span>{paymentLoading ? '...' : `Pay ₹${dmPaymentRequired.price || 5}`}</span>
              </button>
            </div>
          )}
          {/* Smart Replies */}
          {!newMsg && messages.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-hide" data-testid="smart-replies">
              {SMART_REPLIES.map(r => (
                <button key={r} onClick={() => setNewMsg(r)}
                  className="shrink-0 bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1.5 text-[11px] text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors"
                  data-testid={`smart-reply-${r.slice(0,10)}`}>{r}</button>
              ))}
            </div>
          )}
          <div className="flex items-center space-x-2">
            {/* Free file share */}
            <label className="w-10 h-10 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center cursor-pointer shrink-0 transition-colors" data-testid="dm-file-btn">
              <Paperclip className="w-4 h-4 text-zinc-400" />
              <input type="file" className="hidden" onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f || !activeChat) return;
                const fd = new FormData();
                fd.append('file', f);
                try {
                  const r = await fetch(`${API_URL}/api/dm/send-file?recipient_id=${activeChat.user.user_id}`, {
                    method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd,
                  });
                  if (r.ok) {
                    const dm = await r.json();
                    setMessages(prev => [...prev, dm]);
                    setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }), 50);
                    showToast?.('File sent (free)');
                  } else showToast?.('Failed to send file');
                } catch { showToast?.('Network error'); }
                e.target.value = '';
              }} />
            </label>
            <input type="text" value={newMsg} onChange={e => setNewMsg(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
              placeholder="Type a message..." className="flex-1 bg-zinc-900 border border-zinc-800 rounded-full px-4 py-2.5 text-sm outline-none focus:border-zinc-600"
              data-testid="chat-input" />
            <button onClick={sendMessage} disabled={!newMsg.trim() || sending}
              className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center disabled:opacity-30 shrink-0"
              data-testid="chat-send"><Send className="w-4 h-4" /></button>
            {!newMsg.trim() && (
              <button onClick={recording ? stopRecording : startRecording}
                className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors ${recording ? 'bg-red-500 text-white animate-pulse' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
                data-testid="voice-note-btn">
                {recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== RENDER: Active Room Chat =====
  if (activeRoom) {
    return (
      <div className="h-full flex flex-col bg-black" data-testid="active-room">
        <div className="flex items-center space-x-3 px-4 py-3 border-b border-zinc-800 shrink-0">
          <button onClick={() => setActiveRoom(null)} data-testid="room-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="w-9 h-9 rounded-full bg-indigo-500/20 flex items-center justify-center shrink-0">
            <Hash className="w-5 h-5 text-indigo-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate">{activeRoom.name}</p>
            <p className="text-[10px] text-zinc-500">{activeRoom.member_count} members</p>
          </div>
          <button onClick={() => leaveRoom(activeRoom.room_id)} className="p-2 text-zinc-500 hover:text-red-400" data-testid="leave-room-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {roomMessages.map(msg => {
            const isMine = msg.sender_id === user.user_id;
            return (
              <div key={msg.message_id} className="flex items-start space-x-2.5">
                {!isMine && (
                  <button onClick={() => onProfileClick?.(msg.sender_id)} className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center overflow-hidden shrink-0 mt-0.5">
                    {msg.sender_picture ? <img src={msg.sender_picture} alt="" className="w-full h-full object-cover" /> :
                      <span className="text-[10px] font-bold">{msg.sender_name?.[0]}</span>}
                  </button>
                )}
                <div className={`flex-1 ${isMine ? 'text-right' : ''}`}>
                  {!isMine && <p className="text-[10px] text-zinc-500 mb-0.5">{msg.sender_name}</p>}
                  <div className={`inline-block max-w-[80%] rounded-2xl px-3.5 py-2 ${isMine ? 'bg-indigo-600 text-white rounded-br-sm ml-auto' : 'bg-zinc-800 text-white rounded-bl-sm'}`}>
                    <p className="text-sm">{msg.content}</p>
                  </div>
                  <p className={`text-[9px] text-zinc-600 mt-0.5 ${isMine ? 'text-right' : ''}`}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={messagesEnd} />
        </div>

        <div className="px-4 py-3 border-t border-zinc-800 shrink-0">
          <div className="flex items-center space-x-2">
            <input type="text" value={newMsg} onChange={e => setNewMsg(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') sendRoomMessage(); }}
              placeholder={`Message #${activeRoom.name}...`}
              className="flex-1 bg-zinc-900 border border-zinc-800 rounded-full px-4 py-2.5 text-sm outline-none focus:border-indigo-500/50"
              data-testid="room-input" />
            <button onClick={sendRoomMessage} disabled={!newMsg.trim() || sending}
              className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center disabled:opacity-30 shrink-0"
              data-testid="room-send"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    );
  }

  // ===== RENDER: Active Community =====
  if (activeCommunity) {
    const isOwner = activeCommunity.owner_id === user.user_id;
    return (
      <div className="h-full flex flex-col bg-black" data-testid="active-community">
        <div className="flex items-center space-x-3 px-4 py-3 border-b border-zinc-800 shrink-0">
          <button onClick={() => setActiveCommunity(null)} data-testid="community-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
            <Megaphone className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate">{activeCommunity.name}</p>
            <p className="text-[10px] text-zinc-500">{activeCommunity.member_count} members · {isOwner ? 'Owner' : 'Member'}</p>
          </div>
          {!isOwner && (
            <button onClick={() => setShowExitConfirm(activeCommunity.community_id)} className="p-2 text-zinc-500 hover:text-red-400" data-testid="leave-community-btn">
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {!isOwner && (
            <div className="text-center py-3">
              <p className="text-[11px] text-zinc-500 bg-zinc-900 inline-block px-4 py-1.5 rounded-full">Broadcast-only channel — only the owner can post</p>
            </div>
          )}
          {communityBroadcasts.length === 0 && (
            <div className="text-center py-12 text-zinc-600">
              <Megaphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No broadcasts yet</p>
            </div>
          )}
          {communityBroadcasts.map(b => {
            const mediaSrc = b.media_url
              ? (b.media_url.startsWith('/uploads/') ? `${API_URL}/api${b.media_url}` : `${API_URL}${b.media_url}`)
              : null;
            return (
            <div key={b.broadcast_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center overflow-hidden">
                  {b.owner_picture ? <img src={b.owner_picture} alt="" className="w-full h-full object-cover" /> :
                    <span className="text-[10px] font-bold text-amber-400">{b.owner_name?.[0]}</span>}
                </div>
                <span className="text-xs font-semibold text-white">{b.owner_name}</span>
                <span className="text-[9px] text-zinc-600">{new Date(b.created_at).toLocaleDateString()}</span>
              </div>
              {mediaSrc && (
                <div className="rounded-xl overflow-hidden mb-2">
                  {b.media_type === 'video' ? (
                    <video src={mediaSrc} controls className="w-full max-h-64 object-contain bg-black" />
                  ) : (
                    <img src={mediaSrc} alt="" className="w-full max-h-64 object-cover" data-testid={`broadcast-media-${b.broadcast_id}`} />
                  )}
                </div>
              )}
              {b.content && <p className="text-sm text-zinc-200 leading-relaxed">{b.content}</p>}
            </div>
            );
          })}
          <div ref={messagesEnd} />
        </div>

        {/* Only owner can broadcast — text, images, videos */}
        {isOwner && (
          <div className="px-4 py-3 border-t border-zinc-800 shrink-0" data-testid="broadcast-compose">
            <div className="flex items-center space-x-2">
              <label className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center cursor-pointer hover:bg-zinc-700 transition-colors shrink-0">
                <ImageIcon className="w-4 h-4 text-zinc-400" />
                <input type="file" accept="image/*,video/*" className="hidden" onChange={e => {
                  const file = e.target.files[0];
                  if (file) {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('content', newMsg || '');
                    const token2 = localStorage.getItem('token');
                    fetch(`${API_URL}/api/communities/${activeCommunity.community_id}/broadcast/media`, {
                      method: 'POST', headers: { 'Authorization': `Bearer ${token2}` }, body: formData,
                    }).then(r => { if (r.ok) { setNewMsg(''); fetchBroadcasts(activeCommunity.community_id); } });
                  }
                }} data-testid="broadcast-media-input" />
              </label>
              <input type="text" value={newMsg} onChange={e => setNewMsg(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') sendBroadcast(); }}
                placeholder="Broadcast text, or add media..."
                className="flex-1 bg-zinc-900 border border-zinc-800 rounded-full px-4 py-2.5 text-sm outline-none focus:border-amber-500/50"
                data-testid="broadcast-input" />
              <button onClick={sendBroadcast} disabled={!newMsg.trim() || sending}
                className="w-10 h-10 rounded-full bg-amber-600 text-white flex items-center justify-center disabled:opacity-30 shrink-0"
                data-testid="broadcast-send"><Send className="w-4 h-4" /></button>
            </div>
          </div>
        )}
        <ExitConfirmModal />
      </div>
    );
  }

  // ===== RENDER: Main List View =====
  return (
    <div className="h-full overflow-y-auto bg-black" data-testid="chat-tab">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-black/95 backdrop-blur-sm border-b border-zinc-800">
        <div className="px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">Messages</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowChatSearch(!showChatSearch)}
              className="p-2 text-zinc-400 hover:text-white transition-colors rounded-full hover:bg-zinc-800" data-testid="chat-search-btn">
              <Search className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Chat Search */}
        {showChatSearch && (
          <div className="px-4 py-2 border-b border-zinc-800">
            <div className="relative">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={chatSearchQuery} onChange={e => setChatSearchQuery(e.target.value)} autoFocus
                placeholder="Search conversations..." className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-9 pr-8 py-2 text-sm outline-none text-white placeholder-zinc-600"
                data-testid="chat-search-input" />
              {chatSearchQuery && <button onClick={() => setChatSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-zinc-500" /></button>}
            </div>
          </div>
        )}
        {/* Tab Switcher */}
        <div className="flex px-4 pb-0">
          {[
            { id: 'dms', label: 'DMs', icon: MessageCircle, count: conversations.length },
            { id: 'rooms', label: 'Rooms', icon: Hash, count: joinedRooms.length },
            { id: 'communities', label: 'Communities', icon: Megaphone, count: joinedCommunities.length },
          ].map(tab => (
            <button key={tab.id} onClick={() => setChatView(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-all border-b-2 ${chatView === tab.id ? 'border-white text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
              data-testid={`chat-view-${tab.id}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && <span className="text-[10px] text-zinc-600 ml-0.5">({tab.count})</span>}
            </button>
          ))}
        </div>
      </div>

      {/* DMs List */}
      {chatView === 'dms' && (
        <>
          {/* Birthday Banner */}
          {birthdaysToday.length > 0 && (
            <div className="px-4 py-3 border-b border-zinc-800/50" data-testid="birthday-banner">
              <div className="bg-gradient-to-r from-pink-500/15 via-amber-500/15 to-purple-500/15 border border-pink-500/20 rounded-2xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🎂</span>
                  <p className="text-xs font-bold text-pink-300 uppercase tracking-wider">Birthdays Today</p>
                </div>
                <div className="space-y-2">
                  {birthdaysToday.map(b => (
                    <div key={b.user_id} className="flex items-center justify-between bg-zinc-900/50 rounded-xl px-3 py-2" data-testid={`bday-${b.user_id}`}>
                      <div className="flex items-center gap-2.5 min-w-0 flex-1 cursor-pointer" onClick={() => onProfileClick?.(b.user_id)}>
                        <div className="w-8 h-8 rounded-full bg-pink-500/20 overflow-hidden shrink-0">
                          {b.picture ? <img src={b.picture} alt="" className="w-full h-full object-cover" /> :
                            <span className="w-full h-full flex items-center justify-center text-[11px] font-bold text-pink-400">{b.name?.[0]}</span>}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-white truncate">{b.name}</p>
                          <p className="text-[10px] text-zinc-500">@{b.username}</p>
                        </div>
                      </div>
                      <button onClick={() => sendBirthdayWish(b.user_id)} disabled={bdayWishing === b.user_id}
                        className="px-3 py-1.5 bg-pink-600 hover:bg-pink-500 text-white text-[11px] font-bold rounded-full shrink-0 disabled:opacity-50"
                        data-testid={`wish-bday-${b.user_id}`}>
                        {bdayWishing === b.user_id ? '...' : '🎉 Wish'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-20 text-zinc-500 px-6">
              <MessageCircle className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
              <p className="text-sm mb-1">No conversations yet</p>
              <p className="text-xs text-zinc-600">Find an expert and send a paid DM to start</p>
            </div>
          ) : (
            <div>{conversations.filter(c => !chatSearchQuery || c.user?.name?.toLowerCase().includes(chatSearchQuery.toLowerCase())).map((convo, i) => (
              <button key={i} onClick={() => openChat(convo)}
                className="w-full flex items-center space-x-3 px-4 py-3.5 border-b border-zinc-800/50 hover:bg-zinc-900/50 transition-colors text-left"
                data-testid={`convo-${convo.user?.user_id}`}>
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-orange-400 p-[2px] shrink-0">
                  <div className="w-full h-full rounded-full bg-black flex items-center justify-center overflow-hidden">
                    {convo.user?.picture ? <img src={convo.user.picture} alt="" className="w-full h-full object-cover" /> :
                      <span className="text-sm font-bold">{convo.user?.name?.[0]}</span>}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-sm truncate">{convo.user?.name}</p>
                    <span className="text-[10px] text-zinc-600 shrink-0">{new Date(convo.last_time).toLocaleDateString()}</span>
                  </div>
                  <p className="text-xs text-zinc-500 truncate">{convo.last_dm_type === 'voice_note' ? 'Voice Note' : convo.last_message}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-700 shrink-0" />
              </button>
            ))}</div>
          )}
        </>
      )}

      {/* Rooms List */}
      {chatView === 'rooms' && (
        <div>
          {/* Search bar */}
          <div className="px-4 py-3">
            <div className="relative">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" value={roomSearch} onChange={e => searchRooms(e.target.value)}
                placeholder="Search subject rooms..."
                className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none focus:border-indigo-500/50 placeholder-zinc-600"
                data-testid="room-search" />
            </div>
          </div>

          {/* Search results */}
          {roomSearch.trim() && (
            <div className="px-4 pb-3">
              <p className="text-[10px] text-zinc-500 mb-2 uppercase tracking-wider">Search Results</p>
              {searchingRooms ? (
                <div className="flex justify-center py-4"><Loader className="w-5 h-5 text-zinc-500 animate-spin" /></div>
              ) : searchedRooms.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-xs text-zinc-500 mb-3">No rooms found for "<span className="text-white font-semibold">{roomSearch}</span>"</p>
                  <button onClick={createCustomRoom}
                    className="inline-flex items-center gap-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-xs font-bold px-4 py-2.5 rounded-full transition-all shadow-lg shadow-indigo-600/20"
                    data-testid="create-new-room-btn">
                    <Plus className="w-3.5 h-3.5" /> Create New Room
                  </button>
                  <p className="text-[10px] text-zinc-600 mt-2">Be the first to start "{roomSearch}"</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {searchedRooms.map(room => (
                    <div key={room.room_id} className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5">
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <Hash className="w-4 h-4 text-indigo-400 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-white truncate">{room.name}</p>
                          <p className="text-[10px] text-zinc-500 truncate">{room.subject_path}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] text-zinc-600">{room.member_count} members</span>
                        {room.is_joined ? (
                          <button onClick={() => openRoom(room)} className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg" data-testid={`open-room-${room.room_id}`}>Open</button>
                        ) : (
                          <button onClick={() => joinRoom(room.room_id)} className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-xs font-medium rounded-lg" data-testid={`join-room-${room.room_id}`}>
                            <UserPlus className="w-3 h-3 inline mr-1" />Join
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Joined rooms */}
          <div className="px-4">
            <p className="text-[10px] text-zinc-500 mb-2 uppercase tracking-wider">Your Rooms</p>
            {joinedRooms.length === 0 ? (
              <div className="text-center py-12 text-zinc-600">
                <Hash className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No rooms joined yet</p>
                <p className="text-xs text-zinc-700 mt-1">Search for a subject to find rooms</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {joinedRooms.map(room => (
                  <button key={room.room_id} onClick={() => openRoom(room)}
                    className="w-full flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-3 hover:bg-zinc-800/80 transition-colors text-left"
                    data-testid={`joined-room-${room.room_id}`}>
                    <div className="w-10 h-10 rounded-xl bg-indigo-500/15 flex items-center justify-center shrink-0">
                      <Hash className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{room.name}</p>
                      <p className="text-[10px] text-zinc-500">{room.member_count} members · {room.message_count} messages</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-700 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Communities List — My Community + Communities I Follow */}
      {chatView === 'communities' && (
        <div className="px-4 py-3">
          {/* My Community — Pinned at Top */}
          <div className="mb-4" data-testid="my-community-section">
            <p className="text-[10px] text-zinc-500 mb-2 uppercase tracking-wider font-bold">My Community</p>
            {(() => {
              const myCommunity = joinedCommunities.find(c => c.owner_id === user.user_id);
              if (myCommunity) {
                return (
                  <button onClick={() => openCommunity(myCommunity)}
                    className="w-full flex items-center gap-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl px-3 py-3 hover:from-amber-500/15 hover:to-orange-500/15 transition-colors text-left"
                    data-testid="my-community-row">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0 overflow-hidden">
                      {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <Megaphone className="w-5 h-5 text-amber-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">My Community</p>
                      <p className="text-[10px] text-zinc-400">{myCommunity.member_count} members</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-amber-500/50 shrink-0" />
                  </button>
                );
              }
              return (
                <button onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/communities`, {
                      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
                    });
                    if (res.ok) {
                      const newComm = await res.json();
                      await fetchJoinedCommunities();
                      // Auto-open the newly created community so owner can start broadcasting
                      if (newComm && newComm.community_id) {
                        openCommunity(newComm);
                      }
                      showToast?.('Community created — start broadcasting!');
                    }
                  } catch {}
                }}
                  className="w-full flex items-center gap-3 bg-zinc-900/50 border border-dashed border-zinc-700 rounded-xl px-3 py-4 hover:bg-zinc-800/50 transition-colors text-left"
                  data-testid="start-community-btn">
                  <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center shrink-0">
                    <Plus className="w-5 h-5 text-zinc-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-zinc-300">Start Your Community</p>
                    <p className="text-[10px] text-zinc-600">Create a broadcast channel for your followers</p>
                  </div>
                </button>
              );
            })()}
          </div>

          {/* Divider */}
          <div className="border-t border-zinc-800 mb-4" />

          {/* Communities I Follow */}
          <p className="text-[10px] text-zinc-500 mb-2 uppercase tracking-wider">Communities I Follow</p>
          {joinedCommunities.filter(c => c.owner_id !== user.user_id).length === 0 ? (
            <div className="text-center py-8 text-zinc-600">
              <Megaphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No communities joined yet</p>
              <p className="text-xs text-zinc-700 mt-1">Visit a user's profile to join their community</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {joinedCommunities.filter(c => c.owner_id !== user.user_id).map(comm => (
                <button key={comm.community_id} onClick={() => openCommunity(comm)}
                  className="w-full flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-3 hover:bg-zinc-800/80 transition-colors text-left"
                  data-testid={`joined-comm-${comm.community_id}`}>
                  <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
                    <Megaphone className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{comm.name}</p>
                    <p className="text-[10px] text-zinc-500">{comm.member_count} members · {comm.broadcast_count} broadcasts</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-700 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Exit Community Confirmation */}
      <ExitConfirmModal />
    </div>
  );
}
